/**
 * @file: useCanRollbackOrder.ts
 * @copyright: (c) 2019-2021 sichuan zhichetech co., ltd.
 */

import { useCurrentUserDangerously } from '@euler/app/flows/auth/context';
import { useOrderContext } from '@euler/app/flows/order/functions/OrderContext';
import { useAppLoading } from '@euler/components/loading/Provider';
import { OrgUserRoleHelper } from '@euler/functions/OrgUserRoleHelper';
import { useStoreConfiguration } from '@euler/functions/useStoreConfiguration';
import { HttpError } from '@euler/lib/request';
import { useServiceFactory } from '@euler/services/factory';
import { showAlert, withConfirmation, withPrompt } from '@euler/utils';
import { usePersistFn } from '@euler/utils/hooks';

export const useCancelOrder = (params: {
  orderNo: string;
  taskNo: string;
  isMainOrder: boolean;
}) => {
  const { orderNo, taskNo, isMainOrder } = params;
  const appLoading = useAppLoading();
  const orderContext = useOrderContext();
  const storeConfiguration = useStoreConfiguration();
  const {
    user: { role },
  } = useCurrentUserDangerously();
  const roleHelper = new OrgUserRoleHelper(role);
  const canCancelOrder = storeConfiguration.restrictedRolesForCancelOrder
    ?.length
    ? roleHelper.hasAnyRole(...storeConfiguration.restrictedRolesForCancelOrder)
    : true;

  const { orderService } = useServiceFactory();

  const cancelOrder = usePersistFn(async () => {
    if (!canCancelOrder) return;

    const reason = await withPrompt({
      title: '闭单原因',
      message: '为做好服务记录，请输入闭单原因',
      confirmButtonStyle: 'destructive',
      confirmButtonText: '确认闭单',
      cancelButtonText: '取消',
    });

    if (!reason?.trim()) {
      return;
    }

    if (
      !(await withConfirmation({
        title: '取消订单',
        message: '您即将取消当前订单，取消订单后将无法恢复，是否确认取消？',
      }))
    ) {
      return;
    }

    await appLoading.showWithAction(async () => {
      try {
        await orderService.cancelOrder(orderNo, {
          reason: reason.trim(),
        });
        orderContext.event$.next({
          type: 'canceled',
          orderNo,
          taskNo,
          extra: reason,
          isMainOrder,
        });
      } catch (e) {
        const err = e as HttpError;
        if (err.subCode === 'restricted_roles_for_cancel_order') {
          void showAlert('闭单失败', '对不起，您没有权限取消订单！');
        } else {
          void showAlert(
            '出错了',
            '对不起，闭单失败，当前服务不可用，请稍后再试！\n' + err.message,
          );
        }
      }
    });
  });

  return { canCancelOrder, cancelOrder };
};
